import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  DatePicker,
  Input,
  InputNumber,
  Select,
  Spin,
  Steps,
  Table,
  Tag,
  Upload,
  message,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { Button, Icon } from "../../../components/Component";
import { assetApis, AssetPayload } from "../../../api/Assets";
import { documentApis } from "../../../api/Documents";
import adminOrgApis from "../../../api/master/adminOrg";
import AssetStatusModal, { statusTagColor, statusLabel } from "./AssetStatusModal";
import "./AssetEditor.css";

/**
 * Asset editor — the sectioned asset form (per the reference asset-tracker
 * screenshots) as a LEFT-RAIL STEPPER, matching the equipment-rate editor and
 * the task-create flow: rail on the left, one section at a time in an
 * internally-scrolling card, a status/photo card on the right with the primary
 * action underneath it.
 *
 * Steps: Asset Details · Manufacturer · Technical · Maintenance · Documents.
 *
 * DOCUMENTS AND THE PHOTO GO STRAIGHT TO DMS — dms/upload-multi with
 * linkType 'asset' + linkTo assetId, listed via dms/docsListPagination
 * (modules=asset&linkToFilters=<id>). DMS owns the metadata; nothing rides the
 * asset endpoints. Consequence: the Documents step needs a SAVED asset (there
 * is no assetId to link to before that), so it is disabled on create.
 *
 * Status is read-only here — every lifecycle change goes through
 * AssetStatusModal, the one write path that keeps assignment fields honest.
 */

/**
 * Mirrors tool_machinery_type_master's three seed rows (verified in the dev
 * DB; MDM owns the table and it has been these three rows since seeding).
 * Replace with a dropdown RPC if the master ever becomes editable.
 */
const TOOL_TYPE_OPTIONS = [
  { value: 2, label: "Plants & Machinery" },
  { value: 1, label: "Tools & Implements" },
  { value: 3, label: "Others" },
];

const OWNERSHIP_OPTIONS = [
  { value: "Owned", label: "Owned" },
  { value: "Hired", label: "Hired" },
];

const FUEL_OPTIONS = [
  { value: "Diesel", label: "Diesel" },
  { value: "Electric", label: "Electric" },
  { value: "Both", label: "Both" },
  { value: "None", label: "None" },
];

const DATE_FORMAT = "DD/MM/YYYY HH:mm";

type FormState = Record<string, any>;

const AssetEditor = ({
  assetId,
  onClose,
  onSaved,
}: {
  /** null = create */
  assetId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const isEdit = !!assetId;
  const [form, setForm] = useState<FormState>({});
  const [assetRow, setAssetRow] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [uomOptions, setUomOptions] = useState<any[]>([]);

  // documents (DMS)
  const [docs, setDocs] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // The card has no internal scroll (it grows with its content), so on a step
  // change just make sure its top is in view — 'nearest' is a no-op when it
  // already is, and a minimal page scroll when the user was deep in a long
  // section.
  const stepCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    stepCardRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [activeStep]);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminOrgApis.fetchUom();
        const list: any[] = res?.data || [];
        setUomOptions(
          list.map((uom: any) => ({ value: Number(uom.id), label: uom.uomName })),
        );
      } catch {
        /* UOM select stays empty */
      }
    })();
  }, []);

  const fromApi = useCallback((row: any): FormState => {
    const next: FormState = {};
    for (const key of [
      "assetName",
      "category",
      "subCategory",
      "location",
      "serialNumber",
      "poNumber",
      "notes",
      "manufacturer",
      "model",
      "vendorName",
      "vendorContact",
      "manufacturerRemarks",
      "capacity",
      "registrationNumber",
      "engineNumber",
      "chassisNumber",
      "technicalRemarks",
      "ownership",
      "fuelType",
    ]) {
      next[key] = row?.[key] ?? "";
    }
    for (const key of ["toolTypeId", "capacityUomId", "yearOfManufacture"]) {
      next[key] = row?.[key] ? Number(row[key]) : null;
    }
    for (const key of ["purchaseCost", "bhpRating", "powerKw"]) {
      next[key] =
        row?.[key] !== null && row?.[key] !== undefined && row?.[key] !== ""
          ? Number(row[key])
          : null;
    }
    for (const key of [
      "purchaseDate",
      "maintenanceDueDate",
      "warrantyExpiryDate",
      "insuranceExpiryDate",
      "lifecycleExpiryDate",
    ]) {
      next[key] = row?.[key] || null; // ISO string or null
    }
    return next;
  }, []);

  const toPayload = useCallback((): AssetPayload => {
    const payload: AssetPayload = { assetName: (form.assetName || "").trim() };
    const setIf = (key: keyof AssetPayload, value: any) => {
      if (value !== undefined && value !== null && value !== "") (payload as any)[key] = value;
    };
    for (const key of [
      "category",
      "subCategory",
      "location",
      "serialNumber",
      "poNumber",
      "notes",
      "manufacturer",
      "model",
      "vendorName",
      "vendorContact",
      "manufacturerRemarks",
      "capacity",
      "registrationNumber",
      "engineNumber",
      "chassisNumber",
      "technicalRemarks",
      "ownership",
      "fuelType",
      "purchaseDate",
      "maintenanceDueDate",
      "warrantyExpiryDate",
      "insuranceExpiryDate",
      "lifecycleExpiryDate",
    ] as const) {
      setIf(key, form[key]);
    }
    for (const key of [
      "toolTypeId",
      "capacityUomId",
      "yearOfManufacture",
      "purchaseCost",
      "bhpRating",
      "powerKw",
    ] as const) {
      const value = form[key];
      if (value !== undefined && value !== null && value !== "") {
        (payload as any)[key] = Number(value);
      }
    }
    return payload;
  }, [form]);

  // ---- documents (DMS) ---------------------------------------------------

  const loadDocs = useCallback(async () => {
    if (!assetId) return;
    setDocsLoading(true);
    try {
      const res = await documentApis.listDocuments({
        modules: "asset",
        linkToFilters: String(assetId),
        pageSize: 100,
        direction: "P",
        sortOrder: "DESC",
        pageDiff: 0,
      });
      // response shape varies — be defensive
      const data = res?.data ?? res;
      const rows: any[] = data?.list || data?.records || (Array.isArray(data) ? data : []);
      setDocs(rows);
    } catch {
      setDocs([]);
    } finally {
      setDocsLoading(false);
    }
  }, [assetId]);

  const uploadToDms = useCallback(
    async (files: File[], action: "asset-document" | "asset-photo") => {
      if (!assetId || !files.length) return false;
      const formData = new FormData();
      formData.append(
        "filesData",
        JSON.stringify({
          newfiledata: files.map((file, index) => ({
            indexOfFileLocated: index,
            linkType: "asset",
            action,
            docName: file.name,
            linkTo: String(assetId),
          })),
        }),
      );
      files.forEach((file) => formData.append("files", file));
      const res = await documentApis.uploadDocument(formData);
      const ok = !!(res?.status ?? true);
      if (ok) await loadDocs();
      return ok;
    },
    [assetId, loadDocs],
  );

  const docName = (row: any) =>
    row?.customFileName || row?.docName || row?.fileName || row?.originalName || "document";
  const docPath = (row: any) => row?.filePath || row?.fileUrl || row?.path || "";
  const photoRow = docs.find((row) => (row?.moduleFileType || row?.action) === "asset-photo");
  const documentRows = docs.filter(
    (row) => (row?.moduleFileType || row?.action) !== "asset-photo",
  );

  // ---- load --------------------------------------------------------------

  const loadAsset = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const res = await assetApis.getById(assetId);
      const row = res?.data?.asset;
      if (row) {
        setAssetRow(row);
        setForm(fromApi(row));
      }
    } finally {
      setLoading(false);
    }
  }, [assetId, fromApi]);

  useEffect(() => {
    setActiveStep(0);
    if (!isEdit) {
      setForm({});
      setAssetRow(null);
      setDocs([]);
      return;
    }
    loadAsset();
    loadDocs();
  }, [assetId, isEdit, loadAsset, loadDocs]);

  const set = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.assetName?.trim()) {
      message.error("Asset Name is required.");
      setActiveStep(0);
      return;
    }
    setSaving(true);
    try {
      const res = isEdit
        ? await assetApis.update({ ...toPayload(), assetId: assetId as number })
        : await assetApis.create(toPayload());

      if (res?.status) {
        Swal.fire({
          icon: "success",
          title: isEdit ? "Updated" : "Created",
          text:
            res?.data?.assetCode && !isEdit
              ? `Asset ${res.data.assetCode} created. Open it to attach documents and a photo.`
              : res?.data?.message || res?.message,
          timer: 2600,
          showConfirmButton: false,
        });
        onSaved();
      } else {
        Swal.fire({
          icon: "error",
          title: "Could not save",
          text: res?.data?.message || res?.message || "Please try again.",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- field helpers -----------------------------------------------------

  const textField = (key: string, label: string, opts: { required?: boolean; hint?: string; width?: string } = {}) => (
    <div className={`${opts.width ?? "col-md-4"} mb-3`} key={key}>
      <label className="form-label mb-1">
        {label} {opts.required ? <span style={{ color: "#e85347" }}>*</span> : null}
      </label>
      <Input
        value={form[key] || ""}
        onChange={(e) => set(key, e.target.value)}
        placeholder="—"
      />
      {opts.hint ? (
        <div className="text-soft" style={{ fontSize: 11, marginTop: 2 }}>
          {opts.hint}
        </div>
      ) : null}
    </div>
  );

  const numberField = (key: string, label: string, suffix?: string, width = "col-md-4") => (
    <div className={`${width} mb-3`} key={key}>
      <label className="form-label mb-1">{label}</label>
      <InputNumber
        value={form[key]}
        onChange={(v) => set(key, v)}
        min={0}
        style={{ width: "100%" }}
        addonAfter={suffix}
        placeholder="—"
      />
    </div>
  );

  const selectField = (key: string, label: string, options: any[], width = "col-md-4") => (
    <div className={`${width} mb-3`} key={key}>
      <label className="form-label mb-1">{label}</label>
      <Select
        value={form[key] || null}
        onChange={(v) => set(key, v)}
        options={options}
        allowClear
        showSearch
        optionFilterProp="label"
        style={{ width: "100%" }}
        placeholder="—"
      />
    </div>
  );

  const dateField = (key: string, label: string, width = "col-md-4") => (
    <div className={`${width} mb-3`} key={key}>
      <label className="form-label mb-1">{label}</label>
      <DatePicker
        showTime={{ format: "HH:mm" }}
        format={DATE_FORMAT}
        value={form[key] ? dayjs(form[key]) : null}
        onChange={(v) => set(key, v ? v.toISOString() : null)}
        style={{ width: "100%" }}
      />
    </div>
  );

  // ---- steps -------------------------------------------------------------

  const steps = useMemo(
    () => [
      {
        key: "details",
        title: "Asset Details",
        children: (
          <>
            <div className="row">
              {textField("assetName", "Asset Name", {
                required: true,
                hint: "e.g. Drill Jumbo – Unit 1",
              })}
              {selectField("toolTypeId", "Asset Type", TOOL_TYPE_OPTIONS)}
              {textField("category", "Category")}
              {textField("subCategory", "Sub Category")}
              {textField("serialNumber", "Serial Number")}
              {dateField("purchaseDate", "Purchase Date")}
              {numberField("purchaseCost", "Purchase Cost", "₹")}
              {textField("poNumber", "PO Number")}
              {selectField("ownership", "Ownership", OWNERSHIP_OPTIONS)}
              {textField("location", "Location", {
                hint: "Yard / site where it currently sits",
              })}
            </div>
            <div>
              <label className="form-label mb-1">Notes</label>
              <Input.TextArea
                rows={2}
                value={form.notes || ""}
                onChange={(e) => set("notes", e.target.value)}
                maxLength={1000}
              />
            </div>
          </>
        ),
      },
      {
        key: "manufacturer",
        title: "Manufacturer",
        children: (
          <div className="row">
            {textField("manufacturer", "Manufacturer")}
            {textField("model", "Model")}
            {textField("vendorName", "Vendor Name")}
            {textField("vendorContact", "Vendor Contact")}
            <div className="col-12 mb-1">
              <label className="form-label mb-1">Remarks</label>
              <Input.TextArea
                rows={2}
                value={form.manufacturerRemarks || ""}
                onChange={(e) => set("manufacturerRemarks", e.target.value)}
                maxLength={500}
              />
            </div>
          </div>
        ),
      },
      {
        key: "technical",
        title: "Technical",
        children: (
          <div className="row">
            {textField("capacity", "Capacity", { hint: "e.g. 2.0, 500" })}
            {selectField("capacityUomId", "Capacity UOM", uomOptions)}
            {numberField("bhpRating", "BHP Rating", "HP")}
            {numberField("powerKw", "Total Power", "kW")}
            {selectField("fuelType", "Fuel Type", FUEL_OPTIONS)}
            {numberField("yearOfManufacture", "Year of Manufacture")}
            {textField("registrationNumber", "Registration No")}
            {textField("engineNumber", "Engine No")}
            {textField("chassisNumber", "Chassis No")}
            <div className="col-12 mb-1">
              <label className="form-label mb-1">Remarks</label>
              <Input.TextArea
                rows={2}
                value={form.technicalRemarks || ""}
                onChange={(e) => set("technicalRemarks", e.target.value)}
                maxLength={500}
              />
            </div>
          </div>
        ),
      },
      {
        key: "maintenance",
        title: "Maintenance",
        children: (
          <div className="row">
            {dateField("maintenanceDueDate", "Maintenance Due Date", "col-md-6")}
            {dateField("warrantyExpiryDate", "Warranty Expiry Date", "col-md-6")}
            {dateField("insuranceExpiryDate", "Insurance Expiry Date", "col-md-6")}
            {dateField("lifecycleExpiryDate", "Lifecycle Expiry Date", "col-md-6")}
          </div>
        ),
      },
      {
        key: "documents",
        title: "Documents",
        children: !isEdit ? (
          <Alert
            type="info"
            showIcon
            message="Save the asset first"
            description="Documents and the photo are linked to the asset's record, so they can be attached once it exists. Create the asset, then open it to upload files."
          />
        ) : (
          <>
            <Upload.Dragger
              multiple
              fileList={pendingFiles.map(
                (file, index) =>
                  ({ uid: String(index), name: file.name, status: "done" }) as any,
              )}
              beforeUpload={(file) => {
                setPendingFiles((prev) => [...prev, file as any]);
                return false; // collect locally; upload on the button
              }}
              onRemove={(file) => {
                setPendingFiles((prev) =>
                  prev.filter((_, index) => String(index) !== file.uid),
                );
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: "#2557d6" }} />
              </p>
              <p className="ant-upload-text">Choose or drag files here</p>
              <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                Invoices, manuals, fitness certificates, insurance papers…
              </p>
            </Upload.Dragger>

            {pendingFiles.length > 0 && (
              <div className="d-flex justify-content-end mt-2">
                <Button
                  color="primary"
                  size="sm"
                  disabled={uploadingDocs}
                  onClick={async () => {
                    setUploadingDocs(true);
                    try {
                      const ok = await uploadToDms(pendingFiles, "asset-document");
                      if (ok) {
                        message.success(`${pendingFiles.length} file(s) uploaded.`);
                        setPendingFiles([]);
                      } else {
                        message.error("Upload failed — please try again.");
                      }
                    } finally {
                      setUploadingDocs(false);
                    }
                  }}
                >
                  {uploadingDocs
                    ? "Uploading…"
                    : `Upload ${pendingFiles.length} file(s)`}
                </Button>
              </div>
            )}

            <Table
              className="mt-3"
              size="small"
              rowKey={(row: any, index?: number) => String(row?.id ?? row?.docId ?? index)}
              loading={docsLoading}
              dataSource={documentRows}
              locale={{ emptyText: "No documents attached yet" }}
              pagination={false}
              columns={[
                {
                  title: "Document",
                  key: "name",
                  render: (_: any, row: any) =>
                    docPath(row) ? (
                      // plain anchor on purpose — download links stay anchors in
                      // this codebase (global CSS restyles antd Buttons)
                      <a href={docPath(row)} target="_blank" rel="noreferrer">
                        {docName(row)}
                      </a>
                    ) : (
                      docName(row)
                    ),
                },
                {
                  title: "Uploaded",
                  key: "uploaded",
                  width: 180,
                  render: (_: any, row: any) => {
                    const value = row?.createdDate || row?.createdAt || row?.uploadedAt;
                    return value ? dayjs(value).format("DD MMM YYYY HH:mm") : "—";
                  },
                },
              ]}
            />
          </>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, uomOptions, isEdit, pendingFiles, uploadingDocs, docs, docsLoading],
  );

  const isLastStep = activeStep === steps.length - 1;

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spin />
      </div>
    );
  }

  return (
    <div className="card-inner card-inner-lg">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <h5 className="mb-0" style={{ color: "#022c57", fontWeight: 600 }}>
            {isEdit ? assetRow?.assetName || form.assetName || "Asset" : "Add Asset"}
          </h5>
          {isEdit && assetRow?.assetCode ? (
            <Tag color="blue">{assetRow.assetCode}</Tag>
          ) : null}
          {isEdit && assetRow?.status ? (
            <Tag color={statusTagColor(assetRow.status)}>{statusLabel(assetRow.status)}</Tag>
          ) : null}
        </div>
        <Button color="light" size="sm" onClick={onClose}>
          <Icon name="arrow-left" />
          <span>Back</span>
        </Button>
      </div>

      <div className="row">
        {/* ---- left rail ---- */}
        <div className="col-lg-2 mb-3">
          <div className="asset-editor-rail">
            <Steps
              className="asset-editor-steps"
              direction="vertical"
              size="small"
              current={activeStep}
              onChange={(step) => setActiveStep(step)}
              items={steps.map((step, index) => ({
                title: step.title,
                status: index === activeStep ? "process" : "wait",
                description:
                  step.key === "documents" && isEdit
                    ? `${documentRows.length} file(s)`
                    : undefined,
              }))}
            />
          </div>
        </div>

        {/* ---- active section ---- */}
        <div className="col-lg-7 mb-3">
          <div className="asset-editor-step-card" ref={stepCardRef}>
            <h6 className="mb-3" style={{ color: "#022c57" }}>
              {steps[activeStep].title}
            </h6>
            {steps[activeStep].children}

            <div
              className="d-flex justify-content-between"
              style={{ marginTop: 20, paddingTop: 12, borderTop: "1px solid #e4e9f0" }}
            >
              <Button
                color="light"
                size="sm"
                disabled={activeStep === 0}
                onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
              >
                <Icon name="arrow-left" />
                <span>Previous</span>
              </Button>
              {!isLastStep ? (
                <Button
                  color="light"
                  size="sm"
                  className="btn-white"
                  onClick={() => setActiveStep((s) => Math.min(steps.length - 1, s + 1))}
                >
                  <span>Next</span>
                  <Icon name="arrow-right" />
                </Button>
              ) : (
                <Button color="primary" size="sm" disabled={saving} onClick={handleSave}>
                  {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Asset"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* ---- right: status / photo / save ---- */}
        <div className="col-lg-3">
          <div className="asset-editor-side">
            <div
              style={{
                border: "1px solid #dbe3ec",
                borderRadius: 4,
                padding: 16,
                background: "#f7f9fc",
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0" style={{ color: "#022c57" }}>
                  Status
                </h6>
                {isEdit ? (
                  <Button
                    size="sm"
                    color="light"
                    onClick={() => setStatusModalOpen(true)}
                  >
                    Change
                  </Button>
                ) : null}
              </div>

              {!isEdit ? (
                <div className="text-soft" style={{ fontSize: 12.5 }}>
                  New assets start as <Tag color="green">Available</Tag>. Assign,
                  send to maintenance or retire from the list or this panel once
                  saved.
                </div>
              ) : (
                <table className="w-100" style={{ fontSize: 12.5 }}>
                  <tbody>
                    <tr>
                      <td style={{ color: "#5d6f83", padding: "3px 0" }}>Status</td>
                      <td style={{ textAlign: "right" }}>
                        <Tag
                          color={statusTagColor(assetRow?.status)}
                          style={{ marginInlineEnd: 0 }}
                        >
                          {statusLabel(assetRow?.status)}
                        </Tag>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ color: "#5d6f83", padding: "3px 0" }}>Assigned To</td>
                      <td style={{ textAlign: "right" }}>
                        {assetRow?.assignedToName || "—"}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ color: "#5d6f83", padding: "3px 0" }}>Project</td>
                      <td style={{ textAlign: "right" }}>
                        {assetRow?.projectTitle || "—"}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ color: "#5d6f83", padding: "3px 0" }}>Return Date</td>
                      <td style={{ textAlign: "right" }}>
                        {assetRow?.returnDate
                          ? dayjs(assetRow.returnDate).format("DD/MM/YYYY HH:mm")
                          : "—"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}

              {/* ---- photo ---- */}
              <div className="mt-3">
                {photoRow && docPath(photoRow) ? (
                  <img
                    src={docPath(photoRow)}
                    alt={assetRow?.assetName || "asset"}
                    style={{
                      width: "100%",
                      borderRadius: 4,
                      border: "1px solid #dbe3ec",
                      objectFit: "cover",
                      maxHeight: 180,
                    }}
                  />
                ) : (
                  <div
                    className="text-soft d-flex align-items-center justify-content-center"
                    style={{
                      border: "1px dashed #cbd7e4",
                      borderRadius: 4,
                      height: 90,
                      fontSize: 12,
                    }}
                  >
                    No photo yet
                  </div>
                )}
                {isEdit ? (
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={async (file) => {
                      setUploadingPhoto(true);
                      try {
                        const ok = await uploadToDms([file as any], "asset-photo");
                        if (ok) message.success("Photo updated.");
                        else message.error("Photo upload failed.");
                      } finally {
                        setUploadingPhoto(false);
                      }
                      return false;
                    }}
                  >
                    <Button
                      size="sm"
                      color="light"
                      className="w-100 justify-content-center mt-2"
                      disabled={uploadingPhoto}
                    >
                      <Icon name="camera" />
                      <span>{uploadingPhoto ? "Uploading…" : photoRow ? "Replace Photo" : "Add Photo"}</span>
                    </Button>
                  </Upload>
                ) : null}
              </div>
            </div>

            {/* primary action under the card, per the house pattern */}
            <Button
              color="primary"
              className="btn-block w-100 justify-content-center mt-2"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Asset"}
            </Button>
          </div>
        </div>
      </div>

      <AssetStatusModal
        open={statusModalOpen}
        asset={assetRow}
        onClose={() => setStatusModalOpen(false)}
        onDone={loadAsset}
      />
    </div>
  );
};

export default AssetEditor;
